import type { BasePageProps } from '@shared/components/BasePageProps';
import type { FilterConfig } from '@shared/components/Filter.types';
import type { SortOption } from '@shared/components/Filters';
import type { GICSCode } from '@shared/domain/company/GICS.types';
import { gicsTierRank } from '@shared/domain/company/gicsTierRank';
import type { IntelligenceRequest as IntelligenceRequestType } from '@shared/domain/intelligenceRequest/IntelligenceRequest';
import { AutoAwesomeTwoTone, LockTwoTone, PublicTwoTone } from '@mui/icons-material';
import AddCardIcon from '@mui/icons-material/AddCard';
import { Box } from '@mui/material';
import { motion } from 'framer-motion';
import { useContext, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Button from '@shared/components/Buttons/Button';
import Filters from '@shared/components/Filters';
import { IconName } from '@shared/components/Icon';
import InfoBanner from '@shared/components/InfoBanner';
import { LoadingSpinner } from '@shared/components/LoadingSpinner';
import { UserContext, UserLoadState } from '@/shared/contexts/UserContext';
import { IntelligenceRequestStatus } from '@shared/domain/intelligenceRequest/IntelligenceRequestStatus';
import { useIntelligenceRequests } from '@shared/hooks/useIntelligenceRequests';
import { useApi } from '@shared/services/api';
import { useMessageService } from '@/shared/services/MessageService/MessageServiceContext';
import { fetchAllGicsCodes } from '../company/company-search/api/api';
import { EngagementType } from './components/IntelligenceRequest.types';
import TrialExpiredPaywall from '../company/company-profile-page/components/TrialExpiredPaywall';
import CreateIntelligenceRequestModal from '../review-reader/components/CreateIntelligenceRequestModal';
import IntelligenceRequest from './components/IntelligenceRequestCard';
import IntelligenceRequestEmptyState from './components/IntelligenceRequestEmptyState';

interface IntelligenceRequestsPageProps extends BasePageProps {
  embedded?: boolean;
  manageMode?: boolean;
}

function IntelligenceRequests(props: IntelligenceRequestsPageProps) {
  const [sortState, setSortState] = useState({
    orderBy: 'amountUsd',
    orderDirection: 'DESC' as 'ASC' | 'DESC',
  });
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [selectedIndustries, setSelectedIndustries] = useState<string[]>([]);
  const [privateRequestsCount, setPrivateRequestsCount] = useState(0);
  const [publicRequestsCount, setPublicRequestsCount] = useState(0);
  const [curatedRequestsCount, setCuratedRequestsCount] = useState(0);
  const [curatedRequests, setCuratedRequests] = useState<IntelligenceRequestType[]>([]);
  const [isPrivateView, setIsPrivateView] = useState(false);
  const [isCuratedView, setIsCuratedView] = useState(true);
  const [industries, setIndustries] = useState<GICSCode[]>([]);

  const [currentFilters, setCurrentFilters] = useState<
    Record<string, string | string[]>
  >({
    industry: [],
    viewType: 'curated',
  });

  const hasScrolledRef = useRef(false);
  const bootstrappingRef = useRef(true);

  const { user, userLoadState, fetchUsageInfo } = useContext(UserContext);
  const isContributor = user?.planSlug === 'contributor';
  const isTrialExpired = user?.subscriptionStatus === 'trial_expired' || isContributor;
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const scrollToRequestId = searchParams.get('requestId');
  const { openMessage } = useMessageService();

  const { intelligenceRequests, getIntelligenceRequests } =
    useIntelligenceRequests();
  const api = useApi();

  useEffect(() => {
    const loadIndustries = async () => {
      const fetchedGicsCodes = await fetchAllGicsCodes();
      setIndustries(fetchedGicsCodes);
    };

    loadIndustries();
  }, []);

  const filterConfigs: FilterConfig[] = [
    {
      id: 'viewType',
      label: 'View Type',
      type: 'toggle',
      exclusive: true,
      size: 'small',
      showLabel: false,
      options: [
        {
          value: 'curated',
          name: 'Curated For You',
          icon: <AutoAwesomeTwoTone />,
          count: curatedRequestsCount,
          tooltip:
            'Intelligence Requests matched to companies from your deal insights.',
        },
        {
          value: 'public',
          name: 'Public Requests',
          icon: <PublicTwoTone />,
          count: publicRequestsCount,
          tooltip: 'Public Intelligence Requests open to all contributors.',
        },
        {
          value: 'private',
          name: 'Private Requests',
          icon: <LockTwoTone />,
          count: privateRequestsCount,
          tooltip:
            'Private Intelligence Requests are sent directly to you by the requestor and are requests for YOUR specific expertise.',
        },
      ],
      testId: 'view-type-toggle',
    },
    ...(industries.length > 0
      ? [
          {
            id: 'industry',
            label: 'Sector / Industry',
            type: 'select' as const,
            autocomplete: true,
            multiple: true,
            clearable: false,
            size: 'small' as const,
            options: industries
              .sort((a, b) => {
                if (a.tier === b.tier) {
                  return a.name.localeCompare(b.name);
                }
                return gicsTierRank(a.tier) - gicsTierRank(b.tier);
              })
              .map((gics) => ({
                value: gics.code,
                name: gics.name,
                group: gics.tier || 'Other',
              })),
            testId: 'gics-filter-select',
          },
        ]
      : []),
  ];

  const sortOptions: SortOption[] = [
    { id: 'createdAt', label: 'Date Created' },
    { id: 'companyName', label: 'Company Name' },
    { id: 'amountUsd', label: 'Amount (USD)' },
  ];

  const handleFilterChange = (filterId: string, value: string | string[]) => {
    setCurrentFilters((prev) => ({
      ...prev,
      [filterId]: value,
    }));

    if (filterId === 'viewType') {
      setIsPrivateView(value === 'private');
      setIsCuratedView(value === 'curated');
    }

    if (filterId === 'industry' && Array.isArray(value)) {
      setSelectedIndustries(value);
    }
  };

  const handleSortChange = (
    newOrderBy: string,
    newOrderDirection: 'ASC' | 'DESC'
  ) => {
    setSortState({
      orderBy: newOrderBy,
      orderDirection: newOrderDirection,
    });
  };

  const handleClearAllFilters = () => {
    setCurrentFilters({
      industry: [],
      viewType: 'curated',
    });
    setSelectedIndustries([]);
    setIsPrivateView(false);
    setIsCuratedView(true);
  };

  const getEffectiveFilters = () => {
    let filters: Record<string, any> = {
      limit: 300,
      offset: 0,
      orderBy: sortState.orderBy,
      orderDirection: sortState.orderDirection,
      gicsCode: selectedIndustries,
    };
    if (props.manageMode) {
      // Manage mode: show only IRs created by the current user
      filters = {
        ...filters,
        userId: user?.id,
        engagementType: [
          EngagementType.Anonymous,
          EngagementType.Consultation,
          EngagementType.Direct,
          EngagementType.Review,
        ],
      };
    } else {
      // Browse mode: exclude IRs created by the current user
      filters = {
        ...filters,
        excludeUserId: user?.id,
      };
    }
    return filters;
  };

  const handleCreateIntelligenceRequest = () => {
    openMessage({
      modalType: 'action',
      header: 'Request Consultation',
      customBody: (
        <CreateIntelligenceRequestModal onSuccess={refetchCurrentView} />
      ),
      size: 'large',
    });
  };

  const updatePrivateRequestsCount = async () => {
    if (!user?.id) {
      setPrivateRequestsCount(0);
      return;
    }
    try {
      const res = await api.get('/intelligence-request', {
        params: {
          ...getEffectiveFilters(),
          isPrivate: true,
          authorId: user.id, // Only count private requests directed at this user
          status: [
            IntelligenceRequestStatus.ACTIVE,
            IntelligenceRequestStatus.ACCEPTED_BY_REVIEW_AUTHOR,
          ],
        },
      });
      const count = Array.isArray(res?.data) ? res.data.length : 0;
      setPrivateRequestsCount(count);
    } catch {
      setPrivateRequestsCount(0);
    }
  };

  const updatePublicRequestsCount = async () => {
    if (!user?.idToken) {
      setPublicRequestsCount(0);
      return;
    }
    try {
      const publicFilters = {
        ...getEffectiveFilters(),
        isPrivate: false,
        status: IntelligenceRequestStatus.ACTIVE,
      };
      const res = await api.get('/intelligence-request', {
        params: publicFilters,
      });
      const count = Array.isArray(res?.data) ? res.data.length : 0;
      setPublicRequestsCount(count);
    } catch {
      setPublicRequestsCount(0);
    }
  };

  const handleViewPrivateRequests = async (options?: {
    skipCount?: boolean;
  }) => {
    if (!user?.idToken || !user?.id) return;
    const filters = {
      ...getEffectiveFilters(),
      isPrivate: true,
      authorId: user.id, // Only show private requests directed at this user
      status: [
        IntelligenceRequestStatus.ACTIVE,
        IntelligenceRequestStatus.ACCEPTED_BY_REVIEW_AUTHOR,
      ],
    };
    const result = await getIntelligenceRequests(filters);
    setIsPrivateView(true);
    setCurrentFilters((prev) => ({ ...prev, viewType: 'private' }));
    if (!options?.skipCount) {
      // Use the result we just fetched to set the private count (no extra API call)
      const privateCount = Array.isArray(result) ? result.length : 0;
      setPrivateRequestsCount(privateCount);
      // Public count unchanged - no need to re-fetch
    }
  };

  const handleViewCuratedRequests = async () => {
    if (!user?.idToken) return;
    try {
      const res = await api.get('/intelligence-request/curated');
      const list = Array.isArray(res?.data) ? res.data : res?.data?.data || [];
      setCuratedRequests(list as IntelligenceRequestType[]);
      setCuratedRequestsCount(list.length);
      setIsCuratedView(true);
      setIsPrivateView(false);
      setCurrentFilters((prev) => ({ ...prev, viewType: 'curated' }));
    } catch {
      setCuratedRequests([]);
      setCuratedRequestsCount(0);
    }
  };

  const updateCuratedRequestsCount = async () => {
    if (!user?.idToken) {
      setCuratedRequestsCount(0);
      return;
    }
    try {
      const res = await api.get('/intelligence-request/curated');
      const list = Array.isArray(res?.data) ? res.data : res?.data?.data || [];
      setCuratedRequests(list as IntelligenceRequestType[]);
      setCuratedRequestsCount(list.length);
    } catch {
      setCuratedRequestsCount(0);
    }
  };

  const handleViewActiveRequests = async () => {
    if (!user?.idToken) return;
    const filters = {
      ...getEffectiveFilters(),
      isPrivate: false,
      status: IntelligenceRequestStatus.ACTIVE,
    };
    const result = await getIntelligenceRequests(filters);
    setIsPrivateView(false);
    setCurrentFilters((prev) => ({ ...prev, viewType: 'public' }));
    const publicCount = Array.isArray(result) ? result.length : 0;
    setPublicRequestsCount(publicCount);
  };

  const refetchCurrentView = async () => {
    if (props.manageMode) {
      const filters = getEffectiveFilters();
      await getIntelligenceRequests(filters);
    } else if (isCuratedView) {
      await handleViewCuratedRequests();
    } else if (isPrivateView) {
      const filters = {
        ...getEffectiveFilters(),
        isPrivate: true,
        authorId: user?.id,
        status: [
          IntelligenceRequestStatus.ACTIVE,
          IntelligenceRequestStatus.ACCEPTED_BY_REVIEW_AUTHOR,
        ],
      };
      const result = await getIntelligenceRequests(filters);
      const privateCount = Array.isArray(result) ? result.length : 0;
      setPrivateRequestsCount(privateCount);
    } else {
      const filters = {
        ...getEffectiveFilters(),
        isPrivate: false,
        status: IntelligenceRequestStatus.ACTIVE,
      };
      const result = await getIntelligenceRequests(filters);
      const publicCount = Array.isArray(result) ? result.length : 0;
      setPublicRequestsCount(publicCount);
    }
  };

  useEffect(() => {
    if (!user?.idToken) return;
    if (props.manageMode && isTrialExpired) {
      setIsInitialLoad(false);
      return;
    }

    const wantPrivate = searchParams.get('isPrivate') === 'true';

    if (wantPrivate && user?.id) {
      if (bootstrappingRef.current) {
        setIsPrivateView(true);
        setCurrentFilters((prev) => ({ ...prev, viewType: 'private' }));
      }
      handleViewPrivateRequests({ skipCount: true }).then(() => {
        setIsInitialLoad(false);
        bootstrappingRef.current = false;
        updatePrivateRequestsCount();
        updatePublicRequestsCount();
      });
      return;
    }

    // On bootstrap (non-manage, non-private), always default to the
    // Curated For You tab — even when the user has zero curated IRs,
    // we want them to land there so the personalization is the first
    // thing they see post-onboarding.
    const bootstrap = async () => {
      if (!props.manageMode) {
        try {
          const curatedRes = await api.get('/intelligence-request/curated');
          const curatedList = Array.isArray(curatedRes?.data)
            ? curatedRes.data
            : curatedRes?.data?.data || [];
          setCuratedRequests(curatedList as IntelligenceRequestType[]);
          setCuratedRequestsCount(curatedList.length);
        } catch {
          setCuratedRequests([]);
          setCuratedRequestsCount(0);
        }

        if (bootstrappingRef.current) {
          setIsCuratedView(true);
          setIsPrivateView(false);
          setCurrentFilters((prev) => ({ ...prev, viewType: 'curated' }));
          setTimeout(() => setIsInitialLoad(false), 1000);
          bootstrappingRef.current = false;
          updatePrivateRequestsCount();
          updatePublicRequestsCount();
          return;
        }
      }

      const initialFilters = props.manageMode
        ? getEffectiveFilters()
        : {
            ...getEffectiveFilters(),
            isPrivate: false,
            status: IntelligenceRequestStatus.ACTIVE,
          };

      const result = await getIntelligenceRequests(initialFilters);
      setTimeout(() => setIsInitialLoad(false), 1000);
      bootstrappingRef.current = false;

      if (!props.manageMode) {
        const publicCount = Array.isArray(result) ? result.length : 0;
        setPublicRequestsCount(publicCount);
        updatePrivateRequestsCount();
      }
    };

    bootstrap();
  }, [
    user?.idToken,
    sortState.orderBy,
    sortState.orderDirection,
    selectedIndustries,
    searchParams,
    props.manageMode,
  ]);

  useEffect(() => {
    if (!user?.idToken) return;
    if (bootstrappingRef.current) {
      return;
    }

    const viewType = currentFilters.viewType;
    if (viewType === 'curated') {
      handleViewCuratedRequests();
    } else if (viewType === 'private' && user?.id) {
      handleViewPrivateRequests();
    } else if (viewType === 'public') {
      handleViewActiveRequests();
    }
  }, [currentFilters.viewType, user?.idToken, user?.id]);

  useEffect(() => {
    if (userLoadState !== UserLoadState.Authenticated) return;
    fetchUsageInfo();
  }, [userLoadState]);

  useEffect(() => {
    if (userLoadState !== UserLoadState.Authenticated) return;

    if (user?.accountMemberships?.[0]?.roleId !== 1) {
      navigate('/my-reviews');
    }
  }, [userLoadState]);

  useEffect(() => {
    if (userLoadState !== UserLoadState.Authenticated) {
      return;
    }

    if (!scrollToRequestId || hasScrolledRef.current) {
      return;
    }

    const attemptScroll = () => {
      const el = document.querySelector(
        `[data-request-id="${scrollToRequestId}"]`
      );
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        hasScrolledRef.current = true;
      }
    };

    setTimeout(() => {
      attemptScroll();
    }, 2000);
  }, [scrollToRequestId, userLoadState, isInitialLoad]);

  useEffect(() => {
    if (userLoadState === UserLoadState.None) {
      return;
    }

    if (
      userLoadState === UserLoadState.Authenticated &&
      user?.accountMemberships?.[0]?.roleId !== 1
    ) {
      navigate('/my-reviews');
    }
  }, [userLoadState, user]);

  useEffect(() => {
    if (userLoadState !== UserLoadState.Authenticated || !user?.id) return;
    if (!props.manageMode && !isTrialExpired) {
      updatePrivateRequestsCount();
      updatePublicRequestsCount();
      updateCuratedRequestsCount();
    }
  }, [userLoadState, user?.id]);

  if (isInitialLoad) {
    return (
      <LoadingSpinner
        variant="circular"
        size="large"
        message="Loading Intelligence Requests..."
        fullScreen
      />
    );
  }

  if (props.manageMode && isTrialExpired) {
    return (
      <Box
        className={`intelligence-requests-page ${props.embedded ? 'intelligence-requests-page--embedded' : ''}`}
      >
        <TrialExpiredPaywall standalone isContributor={isContributor} />
      </Box>
    );
  }

  return (
    <Box
      className={`intelligence-requests-page ${props.embedded ? 'intelligence-requests-page--embedded' : ''}`}
    >
      {!props.embedded && (
        <header className="intelligence-requests-page__header">
          <p className="intelligence-requests-page__eyebrow">
            {props.manageMode ? 'Your Requests' : 'Marketplace'}
          </p>
          <h1 className="intelligence-requests-page__title">
            Intelligence Requests
          </h1>
          <p className="intelligence-requests-page__subtitle">
            View curated requests or start your own. Gather targeted insights —
            whether it's a company review or expert consultation, get the intel
            you need to make informed decisions.
          </p>
        </header>
      )}

      <>
        {!props.manageMode && (
          <Filters
            filters={filterConfigs}
            onFilterChange={handleFilterChange}
            onClearAllFilters={handleClearAllFilters}
            sortOptions={sortOptions}
            orderBy={sortState.orderBy}
            orderDirection={sortState.orderDirection}
            onSortChange={handleSortChange}
            storagePrefix="intelligence-requests"
            currentFilters={currentFilters}
            sortSize="small"
          />
        )}

        {props.manageMode && (
          <Box
            className={`intelligence-requests-page__actions ${
              props.embedded
                ? 'intelligence-requests-page__actions--embedded'
                : 'intelligence-requests-page__actions--not-embedded'
            }`}
          >
            <Box className="intelligence-requests-page__actions-wrapper">
              <Button
                variantType="addButton"
                size="medium"
                data-testid="intelligence-request-toggle"
                startIcon={<AddCardIcon />}
                onClick={handleCreateIntelligenceRequest}
              >
                New Intelligence Request
              </Button>
            </Box>
          </Box>
        )}

        {(() => {
          const displayRequests = isCuratedView
            ? curatedRequests
            : intelligenceRequests;

          return displayRequests?.length > 0 ? (
            <>
              <Box
                className="intelligence-requests-page__grid-container"
                data-testid="intelligence-requests-grid"
              >
                <Box
                  component={motion.div}
                  key={`${isCuratedView ? 'curated' : isPrivateView ? 'private' : 'public'}-${sortState.orderBy}-${sortState.orderDirection}`}
                  className="grid-card-layout"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  {[...displayRequests]
                    .sort((a, b) => {
                      const statusPriority: Record<string, number> = {
                        [IntelligenceRequestStatus.PENDING]: 1,
                        [IntelligenceRequestStatus.ACTIVE]: 2,
                        [IntelligenceRequestStatus.ACCEPTED_BY_REVIEW_AUTHOR]: 3,
                        [IntelligenceRequestStatus.FULFILLED]: 4,
                        [IntelligenceRequestStatus.PAUSED]: 5,
                        [IntelligenceRequestStatus.CANCELLED]: 6,
                        [IntelligenceRequestStatus.REJECTED]: 7,
                        [IntelligenceRequestStatus.EXPIRED]: 8,
                      };

                      const aPriority = statusPriority[a.status] || 99;
                      const bPriority = statusPriority[b.status] || 99;

                      return aPriority - bPriority;
                    })
                    .map((req, index) => {
                      return (
                        <div key={req.id} data-request-id={req.id}>
                          <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{
                              duration: 0.4,
                              delay: index * 0.05,
                              ease: [0.4, 0, 0.2, 1],
                            }}
                          >
                            <IntelligenceRequest
                              data-testid={`intelligence-request-${index}`}
                              completedRequest={req}
                              alignName="left"
                              highlight={String(req.id) === scrollToRequestId}
                              selected={String(req.id) === scrollToRequestId}
                              onRefetch={refetchCurrentView}
                              manageMode={props.manageMode}
                              showActiveChip={props.manageMode}
                              isCurated={isCuratedView && !!req.curatedForUserId}
                            />
                          </motion.div>
                        </div>
                      );
                    })}
                </Box>
              </Box>
            </>
          ) : (
            <IntelligenceRequestEmptyState
              manageMode={props.manageMode}
              handleCreateIntelligenceRequest={handleCreateIntelligenceRequest}
            />
          );
        })()}
      </>
    </Box>
  );
}

export default IntelligenceRequests;
